import { useMemo } from 'react'
import { Outlet } from 'react-router'
import { Box, useTheme } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useOverlay from '@/hooks/useOverlay'
import { useGetOrgCredentialTypes } from '@/service-library/hooks/org-credential-types'
import { useGetOrgCredentials } from '@/service-library/hooks/org-credentials'
import { isAuthenticated } from '@/utils/org-credentials'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import Breadcrumb from '@/components/breadcrumbs/Breadcrumb'
import Drawer from '@/components/drawer/Drawer'
import PageTitle from '@/components/PageTitle'
import SettingsNavigation from '@/components/settings-navigation/SettingsNavigation'
import { SettingsNavigationItemProps } from '@/components/settings-navigation/SettingsNavigationItem'

const baseRoutes = [
  {
    to: 'my-profile',
    label: 'My Profile',
  },
  {
    to: 'my-preferences',
    label: 'My Preferences',
  },
  {
    to: 'organizations',
    label: 'Organizations',
  },
  {
    to: 'organization-types',
    label: 'Organization Types',
  },
  {
    to: 'email-uploads',
    label: 'Email Uploads',
  },
  {
    to: 'lists',
    label: 'Lists',
  },
  {
    to: 'custom-variables',
    label: 'Custom Variables',
  },
]

export default function GlobalSettingsPage() {
  const theme = useTheme()
  const drawerOverlay = useOverlay({ initiallyOpen: true })
  const featureFlags = useFeatureFlagContext()
  const { rootOrganization } = useCurrentAccountContext()
  const { isAuthorized: canEditBilling } = useStytchIsAuthorized(
    'pd.billing',
    'edit',
  )
  const { isAuthorized: canEditRootOrg } = useStytchIsAuthorized(
    'pd.org',
    'edit',
  )
  const { isAuthorized: canEditUser } = useStytchIsAuthorized('pd.user', 'edit')

  const routes = useMemo(() => {
    const tempRoutes = [...baseRoutes]

    if (featureFlags.customer_page_visible && canEditRootOrg) {
      tempRoutes.splice(2, 0, {
        to: `customer/${rootOrganization.id}`,
        label: 'Subscription Details',
      })
    }
    if (featureFlags.show_billing_page && canEditBilling) {
      tempRoutes.splice(2, 0, {
        to: 'billing',
        label: 'Billing',
      })
    }
    if (canEditUser) {
      tempRoutes.splice(2, 0, {
        to: 'users',
        label: 'Users',
      })
    }
    return tempRoutes
  }, [
    canEditBilling,
    canEditRootOrg,
    canEditUser,
    featureFlags.customer_page_visible,
    featureFlags.show_billing_page,
    rootOrganization.id,
  ])

  const { orgCredentials } = useGetOrgCredentials({
    filters: {
      limit: '1000',
      account_id: rootOrganization.account_id,
    },
    refetchOnWindowFocus: true,
  })

  const { orgCredentialTypes } = useGetOrgCredentialTypes({
    filters: {
      limit: '1000',
    },
  })

  const { usedTypeRoutes, unusedTypeRoutes } = orgCredentialTypes
    .toSorted((a, b) => a.name.localeCompare(b.name))
    .reduce<{
      usedTypeRoutes: SettingsNavigationItemProps[]
      unusedTypeRoutes: SettingsNavigationItemProps[]
    }>(
      (acc, type) => {
        const orgCredentialsForType = orgCredentials.filter(
          (credential) => credential.type_id === type.id,
        )
        if (orgCredentialsForType.length > 0) {
          acc.usedTypeRoutes.push({
            to: `integrations/${type.id}`,
            label: type.name,
            persistentBarColor: orgCredentialsForType.some(
              (c) => !isAuthenticated({ ...c, type }),
            )
              ? theme.palette.secondary.main
              : undefined,
          })
        } else {
          acc.unusedTypeRoutes.push({
            to: `integrations/${type.id}`,
            label: type.name,
          })
        }
        return acc
      },
      { usedTypeRoutes: [], unusedTypeRoutes: [] },
    )

  return (
    <>
      <PageTitle>Projects</PageTitle>
      <Breadcrumb label="Global Settings" url="/settings" />

      <Drawer overlay={drawerOverlay} size="sm" transparent>
        <SettingsNavigation label="Global Settings" routes={routes} />
        <SettingsNavigation
          label="Integrations"
          routes={usedTypeRoutes.length > 0 ? usedTypeRoutes : unusedTypeRoutes}
          secondaryRoutes={usedTypeRoutes.length > 0 ? unusedTypeRoutes : []}
        />
      </Drawer>

      <Box sx={{ height: '100%', width: '100%', p: 2, pt: 3 }}>
        <Outlet />
      </Box>
    </>
  )
}
